import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { avatarTone, initialsOf } from '../../../common/libs/initials';
import { IconEdit, IconProjects } from '../../../common/icons';
import DeactivateButton from '../../../common/components/deactivate/deactivate-button';
import { clientKeys } from '../queries/keys';
import { clientPaths } from '../queries/paths';
import { CLIENTS_PAGE_SIZE, useClientsList } from '../contexts/clients';
import SearchBox from '../../../common/components/search-box/search-box';

import '../styles/overview.css';

// Card grid, from design/referencias/clients.webp: avatar, name, subtitle, a
// labelled block, and a footer with two facts.
//
// The search box is the reference's, and it exists now that the backend does:
// `?q=` searches name and contact and answers a `total` for what matches, so
// the pager keeps telling the truth. Before tier 12 it was left out on purpose
// — filtering the page already fetched would have said "3" while there were
// forty on the other pages.
//
// Page and search live in the URL: a link to page 3 of "agro" has to survive a
// reload and be worth sending to somebody.

const STATUS_LABEL = {
    2: 'Activo',
    1: 'Inactivo',
    0: 'Eliminado',
};

const DATE_FORMAT = new Intl.DateTimeFormat('es', {
    day: 'numeric', month: 'short', year: 'numeric',
});

// The published contract documents request bodies but not response shapes, so
// the field names come from the model (data/models/org/client.js). Sequelize
// serializes the attribute names — createdAt — while hand-built packages in
// the same API use created_at. Both are read because the contract does not say
// which one arrives, and being wrong here only shows up as an empty date.
function formatDate(client) {
    const raw = client.createdAt ?? client.created_at;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : DATE_FORMAT.format(date);
}

function ClientCard({ client }) {
    const since = formatDate(client);
    // Straight off the list since T12.3: same key and same shape the detail
    // answers, so opening the client shows the same four figures.
    const active = client.projects_summary?.active;

    return (
        <article className="client-card">
            <header className="client-card__head">
                {/* No client carries a picture: the table has no column for one
                    and there is no upload route. Initials are built from the
                    name that does exist. */}
                <span
                    className={`avatar client-card__avatar avatar--tone-${avatarTone(client.name)}`}
                    aria-hidden="true"
                >
                    {initialsOf(client.name)}
                </span>

                <span className="client-card__identity">
                    <span className="client-card__name">{client.name}</span>
                    <span className="client-card__contact">
                        {client.contact || 'Sin contacto'}
                    </span>
                </span>

                <span className="row-actions">
                    <Gate domain="clients:write">
                        <Link
                            to={`/admin/clients/${client.id}`}
                            className="client-card__edit"
                            aria-label={`Editar ${client.name}`}
                            title="Editar"
                        >
                            <IconEdit />
                        </Link>
                    </Gate>
                    <Gate domain="clients:admin">
                        <DeactivateButton
                            name={client.name}
                            noun="cliente"
                            path={clientPaths.detail(client.id)}
                            invalidate={[clientKeys.all]}
                        />
                    </Gate>
                </span>
            </header>

            {/* The notes block only exists when there are notes. A labelled
                box reading "Sin notas" on every card is a third of the card
                spent saying nothing — and it says it loudest on the nine cards
                that all say it at once. */}
            {client.notes && (
                <div className="client-card__body">
                    <p className="client-card__notes">{client.notes}</p>
                </div>
            )}

            <footer className="client-card__foot">
                {active != null && (
                    <span className="client-card__fact">
                        <span className="client-card__block-label">Proyectos</span>
                        <span className="client-card__projects">
                            <IconProjects />
                            {active} {active === 1 ? 'activo' : 'activos'}
                        </span>
                    </span>
                )}

                <span className="client-card__fact">
                    <span className="client-card__block-label">Estado</span>
                    <span className={`client-card__status client-card__status--${client.status}`}>
                        {STATUS_LABEL[client.status] ?? 'Desconocido'}
                    </span>
                </span>

                <span className="client-card__fact">
                    <span className="client-card__block-label">Alta</span>
                    <span className="client-card__value">{since || '—'}</span>
                </span>
            </footer>
        </article>
    );
}

export default function ClientsOverview() {
    const [params, setParams] = useSearchParams();

    const page = Math.max(Number(params.get('page')) || 1, 1);
    const q = params.get('q') || '';
    const offset = (page - 1) * CLIENTS_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = useClientsList({
        limit: CLIENTS_PAGE_SIZE,
        offset,
        q,
    });

    const items = data?.items ?? [];
    const total = data?.pagination?.total ?? 0;
    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / CLIENTS_PAGE_SIZE), 1),
        [total],
    );

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    // Only the first load blanks the screen. Typing in the search box keeps the
    // previous page visible and dimmed — a list that empties on every keystroke
    // is unreadable.
    if (isLoading && !data) {
        return <p className="clients__loading">Cargando clientes...</p>;
    }

    if (error) {
        return (
            <p className="clients__error">
                {error.message || 'No se pudieron cargar los clientes'}
            </p>
        );
    }

    return (
        <section className="clients">
            <header className="clients__header">
                <div className="clients__heading">
                    <span className="clients__count">
                        {total} {total === 1 ? 'cliente' : 'clientes'}
                    </span>
                    <h2 className="clients__title">Clientes</h2>
                </div>

                <Gate domain="clients:write">
                    <Link to="/admin/clients/new" className="clients__new">
                        + Nuevo cliente
                    </Link>
                </Gate>
            </header>

            <SearchBox placeholder="Buscar por nombre o contacto" />

            {!items.length && (
                <p className="clients__empty">
                    {q
                        ? `Ningún cliente coincide con "${q}".`
                        : 'Todavía no hay clientes cargados.'}
                </p>
            )}

            <div className={`clients__grid ${isFetching ? 'clients__grid--refreshing' : ''}`}>
                {items.map(client => <ClientCard key={client.id} client={client} />)}
            </div>

            {total > CLIENTS_PAGE_SIZE && (
                <nav className="clients__pager">
                    <button
                        type="button"
                        className="clients__pager-button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>

                    <span className="clients__pager-position">
                        Página {page} de {lastPage}
                    </span>

                    <button
                        type="button"
                        className="clients__pager-button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </section>
    );
}
